import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Modal } from 'react-bootstrap';
import Button from 'components/button/Button';
import Loader from 'components/loader/Loader';
import useAppModal from 'hooks/useAppModal';
import {
  AgencyDnsSettingStatus,
  ApplicationDnsSetting,
  WhitelabelSubjectApplication,
} from 'models/CustomDomains';
import useFetchAgencyDnsSettings from '../useFetchAgencyDnsSettings';
import useMigrateAgencyDnsSettings from '../useMigrateAgencyDnsSettings';
import {
  CUSTOM_DOMAINS_HELP_URL,
  hasLegacySettings,
  postCustomDomainsModalClosedToLegacyFrame,
  postCustomDomainsUpdatedToLegacyFrame,
} from '../customDomains.utils';
import CustomDomainSection from './CustomDomainSection';
import {
  FinishConnectingText,
  InfoPanel,
  InfoPanelTitle,
  PropagationNote,
} from './CustomDomainModal.styles';

type SectionApplication =
  | WhitelabelSubjectApplication.PLATFORM
  | WhitelabelSubjectApplication.DBS;

// agency.jsp covers both products; a product-scoped page passes just its own
const ALL_SECTION_APPLICATIONS: SectionApplication[] = [
  WhitelabelSubjectApplication.PLATFORM,
  WhitelabelSubjectApplication.DBS,
];

interface CustomDomainModalProps {
  modalId: string;
  // forwarded by agency.jsp; when undefined the mode is derived from the settings
  shouldMigrate?: boolean;
  applications?: SectionApplication[];
}

const CustomDomainModal = ({
  modalId,
  shouldMigrate,
  applications,
}: CustomDomainModalProps) => {
  const { t } = useTranslation();
  const { closeModal } = useAppModal();
  const { dnsSettings, isLoading, isFetching, isError, refetch } =
    useFetchAgencyDnsSettings();
  const { migrateDnsSettings, isMigrating } = useMigrateAgencyDnsSettings();
  const [cancelledSettings, setCancelledSettings] = useState<
    ApplicationDnsSetting[]
  >([]);
  const hasChangesRef = useRef(false);

  const markChanged = () => {
    hasChangesRef.current = true;
  };

  // cancelling only starts an async deletion; the backend still reports PENDING
  const visibleSettings = useMemo(
    () =>
      dnsSettings.filter(
        ({ domain }) =>
          !cancelledSettings.some((cancelled) => cancelled.domain === domain),
      ),
    [dnsSettings, cancelledSettings],
  );
  const clearedApplications = useMemo(
    () => cancelledSettings.map(({ application }) => application),
    [cancelledSettings],
  );

  // only the rendered sections drive the framing; the legacy frame still gets everything
  const sectionApplications = applications ?? ALL_SECTION_APPLICATIONS;
  const sectionSettings = useMemo(
    () =>
      sectionApplications.map((application) =>
        visibleSettings.find((setting) => setting.application === application),
      ),
    [sectionApplications, visibleSettings],
  );

  const legacySettings = sectionSettings.filter(
    (setting) => setting?.status === AgencyDnsSettingStatus.LEGACY,
  ) as ApplicationDnsSetting[];
  const isMigration =
    shouldMigrate ??
    hasLegacySettings(
      sectionSettings.filter(Boolean) as ApplicationDnsSetting[],
    );
  // footer reverts to Close once nothing is left to migrate; the framing stays
  const hasMigrationToValidate = isMigration && legacySettings.length > 0;

  const connectingDomain =
    sectionSettings.find((setting) => setting?.domain)?.domain ||
    t('componentCustomDomains.exampleDomain');

  // reads state at render time, so concurrent per-section removals stay consistent
  useEffect(() => {
    if (visibleSettings.length || clearedApplications.length) {
      postCustomDomainsUpdatedToLegacyFrame(
        visibleSettings,
        clearedApplications,
      );
    }
  }, [visibleSettings, clearedApplications]);

  // agency.jsp reveals the white labeling section, so only a real change earns it
  useEffect(
    () => () => {
      if (hasChangesRef.current) {
        postCustomDomainsModalClosedToLegacyFrame();
      }
    },
    [],
  );

  const handleCancelSetting = (setting: ApplicationDnsSetting) => {
    markChanged();
    setCancelledSettings((current) => [...current, setting]);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Modal.Body>
        <InfoPanel data-testid="custom-domain-info-panel">
          {isMigration ? (
            <>
              <InfoPanelTitle>
                {t('componentCustomDomains.migrateInfoTitle')}
              </InfoPanelTitle>
              <p>
                <Trans
                  i18nKey="componentCustomDomains.migrateInfoLine1"
                  components={{ bold: <strong /> }}
                />
              </p>
              <p>
                <Trans
                  i18nKey="componentCustomDomains.migrateInfoLine2"
                  components={{ bold: <strong /> }}
                />
              </p>
            </>
          ) : (
            <>
              <InfoPanelTitle>
                {t('componentCustomDomains.infoTitle')}
              </InfoPanelTitle>
              <p>{t('componentCustomDomains.infoLine1')}</p>
              <p>{t('componentCustomDomains.infoLine2')}</p>
              <p>
                <Trans
                  i18nKey="componentCustomDomains.infoLine3"
                  components={{
                    helpLink: (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label
                      <a
                        href={CUSTOM_DOMAINS_HELP_URL}
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                />
              </p>
            </>
          )}
          <PropagationNote>
            {t('componentCustomDomains.propagationNote')}
          </PropagationNote>
        </InfoPanel>

        {isError && (
          <Alert bsStyle="danger" data-testid="custom-domain-fetch-error">
            {t('componentCustomDomains.fetchError')}
          </Alert>
        )}

        <FinishConnectingText data-testid="custom-domain-finish-connecting">
          <Trans
            i18nKey="componentCustomDomains.finishConnecting"
            values={{ domain: connectingDomain }}
            components={{ bold: <strong /> }}
          />
        </FinishConnectingText>

        {sectionApplications.map((application, index) => (
          <CustomDomainSection
            key={application}
            application={application}
            dnsSetting={sectionSettings[index]}
            onCheckConfiguration={refetch}
            onCancelSetting={handleCancelSetting}
            onSettingSaved={markChanged}
            isCheckingConfiguration={isFetching}
            isMigration={isMigration}
          />
        ))}
      </Modal.Body>
      <Modal.Footer>
        {hasMigrationToValidate ? (
          <>
            <Button
              onClick={() => closeModal(modalId)}
              disabled={isMigrating}
              data-testid="custom-domain-modal-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              bsStyle="success"
              onClick={() =>
                migrateDnsSettings(legacySettings, { onSuccess: markChanged })
              }
              disabled={isMigrating}
              data-testid="custom-domain-modal-validate"
            >
              {t('componentCustomDomains.validate')}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => closeModal(modalId)}
            data-testid="custom-domain-modal-close"
          >
            {t('common.close')}
          </Button>
        )}
      </Modal.Footer>
    </>
  );
};

CustomDomainModal.defaultProps = {
  shouldMigrate: undefined,
  applications: undefined,
};

export default CustomDomainModal;
